import { configureStore } from '@reduxjs/toolkit'
import { persistStore, persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { combineReducers } from '@reduxjs/toolkit'

// Import reducers
import authSlice from './slices/authSlice'
import uiSlice from './slices/uiSlice'
import themeSlice from './slices/themeSlice'
import studySlice from './slices/studySlice'
import examSlice from './slices/examSlice'
import flashcardSlice from './slices/flashcardSlice'

import pomodoroSlice from './slices/pomodoroSlice'
import gamificationSlice from './slices/gamificationSlice'
import notesSlice from './slices/notesSlice'
import summariesSlice from './slices/summariesSlice'
import summarySlice from './slices/summarySlice'
import timeTechniqueSlice from './slices/timeTechniqueSlice'

// Import API slices

import { gamificationApi } from './api/gamificationApi'
import gamificationMiddleware from './middleware/gamificationMiddleware'

// Persist config
const persistConfig = {
  key: 'root-v9', // Force complete cache clear with new persist config
  storage,
  whitelist: ['auth', 'ui', 'theme', 'study', 'exam', 'notes', 'summaries', 'summary', 'flashcard', 'pomodoro', 'gamification', 'timeTechnique'], // Only persist these reducers
  blacklist: [], // Don't persist these reducers
}

// Root reducer
const rootReducer = combineReducers({
  auth: authSlice,
  ui: uiSlice,
  theme: themeSlice,
  study: studySlice,
  exam: examSlice,
  flashcard: flashcardSlice,

  pomodoro: pomodoroSlice,
  gamification: gamificationSlice,
  notes: notesSlice,
  summaries: summariesSlice,
  summary: summarySlice,
  timeTechnique: timeTechniqueSlice,
  
  // API slices

  [gamificationApi.reducerPath]: gamificationApi.reducer,
})

// Persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer)

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'persist/PAUSE',
          'persist/PURGE',
          'persist/REGISTER',
          'persist/FLUSH',
          'auth/loginSuccess',
          'auth/updateUser',
        ],
        ignoredActionsPaths: [
          'meta.arg', 
          'payload.timestamp',
          'payload.user.createdAt',
          'payload.user.lastLoginAt',
          'payload.user.updatedAt',
          'payload.createdAt',
          'payload.lastModified',
          'payload.updatedAt',
        ],
        ignoredPaths: [
          'items.dates',
          'auth.user.createdAt',
          'auth.user.lastLoginAt',
          'auth.user.updatedAt',
        ],
      },
    }).concat(gamificationApi.middleware, gamificationMiddleware),
  devTools: import.meta.env.DEV,
})

// Persistor
export const persistor = persistStore(store)

// Types
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

// Typed hooks
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux'

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export default store